// import { Editor } from '@tinymce/tinymce-react';
import { Editor as EditorType } from '@tinymce/tinymce-react';
import { IAllProps } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import { getMIMEFromFileName } from '@vindr/utils/converter';
import resizeBase64Image from '@vindr/utils/resizeBlobImage';
import { ComponentType, LegacyRef, Suspense, forwardRef, lazy, memo } from 'react';
import Loading from './Loading';
import cx from '@vindr/utils/helpers/cx';

export const MARGIN_TOP_PAGE = 10; // 5mm
export const MARGIN_RIGHT_PAGE = 10;
export const MARGIN_BOTTOM_PAGE = 5;
export const MARGIN_LEFT_PAGE = 10; // 10mm

export const WIDTH_PRINT_PAGE = 210;
export const HEIGHT_PRINT_PAGE = 297;

export const pagePrintStyle = `
    @page {
      size: ${WIDTH_PRINT_PAGE}mm ${HEIGHT_PRINT_PAGE}mm;
      margin: ${MARGIN_TOP_PAGE}mm ${MARGIN_RIGHT_PAGE}mm ${MARGIN_BOTTOM_PAGE}mm ${MARGIN_LEFT_PAGE}mm;
    }
    body {
      background: #fff;
      font-family: Times New Roman, Times, serif !important;
      color: #000 !important;
      height: max-content;
      padding: 0;
      margin: 1rem;
      line-height: 1.1;
    }
    p {
      margin: 0;
    }
    strong,
    em,
    p,
    span {
      font-size: 1em;
    }
    .signature {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .wrapper .ant-upload-picture-card-wrapper {
      width: auto !important;
    }
    .ant-upload.ant-upload-select {
      border: none !important;
    }
    .ant-upload.ant-upload-select-picture-card {
      background: transparent !important;
    }
    .signature-text {
      display: none !important;
    }
    .tb-edit-text {
      border: none !important;
    }
    ul:not([style*="list-style-type"]) li {
      list-style-type: disc;
    }
    ol:not([style*="list-style-type"]) li {
      list-style-type: decimal;
    }
    li {
      display: list-item;
    }
    img {
      max-width: 100%;
    }
    /* Hidden Caregiver */
    .standard {
      display: none;
    }

    .page-footer {
      position: fixed;
      bottom: 0;
    }

    .page-header {
      position: fixed;
      top: 0;
      width: 100%;
    }

    tfoot td, thead td {
      padding: 0 !important;
    }

    .mceNonEditable {
      user-select: none;
      pointer-events: none;
    }

    @media print {
      thead {
        display: table-header-group;
      }
      tfoot {
        display: table-footer-group;
      }

      button {
        display: none;
      }

      body {
        margin: 0;
      }

      .mce-item-table:not([border]), .mce-item-table:not([border]) caption, .mce-item-table:not([border]) td, .mce-item-table:not([border]) th, .mce-item-table[border="0"], .mce-item-table[border="0"] caption, .mce-item-table[border="0"] td, .mce-item-table[border="0"] th, table[style*="border-width: 0px"], table[style*="border-width: 0px"] caption, table[style*="border-width: 0px"] td, table[style*="border-width: 0px"] th {
        border: 1px dashed transparent;
      }

      table[border]:not([border="0"]):not([style*=border-color]) td, table[border]:not([border="0"]):not([style*=border-color]) th {
        border-color: transparent;
      }

      .mce-content-body audio[data-mce-selected], .mce-content-body embed[data-mce-selected], .mce-content-body img[data-mce-selected], .mce-content-body object[data-mce-selected], .mce-content-body table[data-mce-selected], .mce-content-body video[data-mce-selected] {
        outline: none;
      }

      .mce-content-body div.mce-resizehandle {
        display: none;
      }

      .mce-content-body [contentEditable=false][data-mce-selected] {
        outline: none;
      }

      .mce-content-body .mce-mergetag-affix {
        background: none;
        color: inherit;
      }
    }

    .break-page {
      border-bottom: 1px solid #333;
      clear: both;
    }

    @media print {
      .break-page {
        border: none;
        page-break-before: always;
      }
    }
    tfoot table.mce-item-table.mce-item-table {
      border: none;
    }
  `;

const Editor = lazy(() =>
  import('@tinymce/tinymce-react').then((module): { default: ComponentType<any> } => ({
    default: module.Editor as unknown as ComponentType<any>,
  }))
) as unknown as typeof EditorType;

export type TextEditorProps = IAllProps & {
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  height?: number | string;
  menubar?: boolean;
  onChange?: (value: string) => void;
  resize?: boolean;
  initProps?: IAllProps['init'];
  id?: string;
  onInsertElement?: (value: any) => void;
  className?: string;
  loading?: boolean;
};

const LoadingEditor = memo(() => (
  <div className="flex h-full items-center justify-center bg-white">
    <Loading />
  </div>
));
LoadingEditor.displayName = 'LoadingEditor';

const TextEditor = forwardRef(
  (
    {
      defaultValue,
      value,
      disabled,
      height = 500,
      menubar = true,
      onChange,
      resize = true,
      onKeyDown,
      initProps,
      onInsertElement,
      className,
      loading,
      ...props
    }: TextEditorProps,
    ref: LegacyRef<EditorType>
  ) => {
    if (loading) return <LoadingEditor />;

    return (
      <article
        className={cx('rounded-sm bg-white', className)}
        {...(height && !resize && { style: { height } })}
      >
        <Suspense fallback={<LoadingEditor />}>
          <Editor
            ref={ref}
            disabled={disabled}
            initialValue={defaultValue}
            tinymceScriptSrc={'/tinymce/tinymce.min.js'} // https://www.tiny.cloud/docs/tinymce/6/react-pm-host/
            value={value}
            init={{
              branding: false,
              menubar,
              height,
              resize,
              images_upload_handler: async (blobInfo) => {
                const type = await getMIMEFromFileName(blobInfo.filename());
                const url = `data:${type};base64,` + (await blobInfo.base64());

                return await resizeBase64Image(url, { type });
              },
              content_style: CONTENT_STYLE,
              plugins: [
                'advlist',
                'autolink',
                'lists',
                'link',
                'image',
                'charmap',
                // 'anchor',
                'element',
                'searchreplace',
                'visualblocks',
                'code',
                'fullscreen',
                'insertdatetime',
                'media',
                'table',
                'preview',
                'help',
                'wordcount',
                'template',
                'mergetags',
                'noneditable',
                'preventdelete',
              ],
              toolbar:
                'undo redo | blocks | fontsize | ' +
                'bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'table | removeformat',
              /* templates: [
                {
                  title: 'Date modified example',
                  description: 'Adds a timestamp indicating the last time the document modified.',
                  content:
                    '<p>Last Modified: <time class="mdate">This will be replaced with the date modified.</time></p>',
                },
                {
                  title: 'Replace values example',
                  description:
                    'These values will be replaced when the template is inserted into the editor content.',
                  content: '<p>Name: {{$username}}, StaffID: {{$staffid}}</p>',
                },
                {
                  title: 'Replace values preview example',
                  description:
                    'These values are replaced in the preview, but not when inserted into the editor content.',
                  content: '<p>Name: {$preview_username}, StaffID: {$preview_staffid}</p>',
                },
                {
                  title: 'Replace values preview and content example',
                  description: 'These values are replaced in the preview, and in the content.',
                  content: '<p>Name: {$inboth_username}, StaffID: {$inboth_staffid}</p>',
                },
              ], */
              onInsertElement,
              paste_as_text: true,
              paste_data_images: true,
              ...initProps,
            }}
            onEditorChange={onChange}
            onKeyDown={onKeyDown}
            {...props}
          />
        </Suspense>
      </article>
    );
  }
);

TextEditor.displayName = 'TextEditor';

export default TextEditor;

const CONTENT_STYLE = `
body { font-family: "Times New Roman", Times, serif; } img { max-width: 100%; } table:not([cellpadding]) td, table:not([cellpadding]) th { padding: 0 !important; } p { margin: 0 } ins { background-color: #d4fcbc; } del { background-color: #fbb6c2; } del, ins { text-decoration: none; } del img, ins img { opacity: 0.5 } ins:has(img[src]), del:has(img[src]) { display: inline-flex }
${pagePrintStyle}
`;
